import { Injectable } from '@angular/core';

import { FitnessPlan, setAsideExerciseIdOf } from '@cf-shared/data-model/fitness-plan';
import { CouchDocDraft, deepClone } from '@cf-shared/deep-helpers';
import { LL } from 'i18n/i18n.service';
import { IEditWorkoutsPage } from 'pages/edit-workouts/edit-workouts-interface';
import { ExerciseConfigurationPageResult, IExerciseConfigurationPage } from 'pages/exercise-configuration/exercise-configuration-interface';
import { IExerciseStatsPage } from 'pages/exercise-stats/exercise-stats-interface';
import { NavController } from 'providers/nav-controller';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { ToastController } from 'providers/toast-controller';
import { DatabaseWrapper } from 'services/database-wrapper';
import { LoggingService } from 'services/logging.service';
import { UserDb } from 'services/user-db.service';
import { CoachExerciseChangeService, RecentExerciseHistory } from 'services/coach-exercise-change.service';
import { applyPreparedChange, bodyWeightShareFromSessions, collectCoachChanges, prepareChange, PreparedChange } from 'services/coach-exercise-change';
import { DebugService } from 'services/debug.service';
import { OverviewRow } from './training-overview';
import {
  CoachCarryOutcome, ExerciseSettingsContext, applyExerciseSettingsSave, isSameExerciseSettings, summarizeExerciseSettings,
} from './training-overview-actions';

/** how saving the result of the Exercise Settings window ended */
export enum ConfigurationApplyOutcome {
  /** the window was closed without saving, or nothing was changed */
  NothingToSave = 'NothingToSave',
  Saved = 'Saved',
  /** the program entry was removed or given another exercise meanwhile; nothing was saved */
  ProgramChangedMeanwhile = 'ProgramChangedMeanwhile',
}

/**
 * The menu on an exercise of the Training Overview (#5456, phase 3): Exercise Settings, History
 * and Edit Workouts, all screens that exist already.
 *
 * The grid is shown by two hosts, the Training Overview page and the Training tab on the coach's
 * check-up review, and the menu must behave the same in both, so it lives here. A host only
 * forwards the tap, and has one callback method that hands the settings window's result back to
 * `handleConfigurationResultAsync`.
 *
 * It keeps nothing in memory between opening the settings window and receiving its result: the
 * "before" summary travels in the window's page params. So it still works when the host page was
 * reloaded while the window was open. How the result is saved without undoing what training wrote
 * meanwhile: `training-overview-actions.ts`.
 */
@Injectable({ providedIn: 'root' })
export class TrainingOverviewActionsService {
  constructor(
    private userDb: UserDb,
    private navCtrl: NavController,
    private popupMenu: PopupMenuProvider,
    private toastCtrl: ToastController,
    private logging: LoggingService,
    private coachExerciseChange: CoachExerciseChangeService,
    private debug: DebugService,
  ) {}

  /**
   * Opens the menu for one row at the tapped element.
   *
   * @param returnCallback the name of the HOST PAGE's method that receives the settings window's
   *   result; a return callback is resolved on the page, never on a component inside it
   * @param onProgramChanged the host's own reload, called when the row turns out to be stale
   */
  async openRowMenuAsync(row: OverviewRow, event: Event, returnCallback: string, onProgramChanged: () => void): Promise<void> {
    const buttons: PopupMenuButton[] = [];
    // if the exercise no longer exists, the settings window would pick a stand-in exercise and
    // file its settings under that one, and the History page refuses to open: offer neither
    if (row.isInProgram && !row.isExerciseMissing) {
      buttons.push({
        text: LL.trainingOverview.menu.exerciseSettings(),
        icon: 'settings',
        handler: () => { void this.openExerciseSettingsAsync(row, returnCallback, onProgramChanged); },
      });
    }
    if (!row.isExerciseMissing) {
      buttons.push({
        text: LL.trainingOverview.menu.history(),
        icon: 'time',
        handler: () => { void this.navCtrl.pushAsync(IExerciseStatsPage, { id: row.exerciseId, mode: 'log' }); },
      });
    }
    if (row.isInProgram) {
      buttons.push({
        text: LL.trainingOverview.menu.editWorkouts(),
        icon: 'assignment',
        // no return callback: the host redraws from its own reload when the program is saved
        handler: () => { void this.navCtrl.pushAsync(IEditWorkoutsPage, { mode: 'edit' }); },
      });
    }
    if (buttons.length === 0) return;
    await this.popupMenu.createMenu({ targetFromElement: event.currentTarget as HTMLElement ?? event.target as HTMLElement, buttons });
  }

  /**
   * Saves what the Exercise Settings window handed back and tells the person when nothing could
   * be saved. Never throws: a failed save is reported here, so a host only has to reload its
   * model afterwards.
   */
  async handleConfigurationResultAsync(context: ExerciseSettingsContext | undefined, result: ExerciseConfigurationPageResult | undefined): Promise<void> {
    try {
      const outcome = await this.applyConfigurationResultAsync(context, result);
      if (outcome === ConfigurationApplyOutcome.ProgramChangedMeanwhile) {
        this.toastCtrl.showToast({ message: LL.trainingOverview.programChanged() });
      }
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.trainingOverview.saveFailed());
    }
  }

  /**
   * Carries the edited settings onto the plan and fitness state as they are stored NOW, and saves
   * both the way Edit Workouts does: a plan backup log first, then the plan and the state under
   * one transaction id. Errors travel up.
   */
  private async applyConfigurationResultAsync(context: ExerciseSettingsContext | undefined, result: ExerciseConfigurationPageResult | undefined): Promise<ConfigurationApplyOutcome> {
    // if the window was closed without Save, or nothing was edited
    if (!result?.saved || !result.modified) {
      return ConfigurationApplyOutcome.NothingToSave;
    }
    // the menu always sends the context along; a saved edit that comes back without it cannot be
    // applied, and saying "nothing to save" would drop the edit without a word
    if (!context?.subSlotId || !context.exerciseId || !context.before) {
      throw new Error('training overview: Exercise Settings was saved but its return context did not come back');
    }
    const after = summarizeExerciseSettings(
      result.fitnessPlan?.subSlots?.[context.subSlotId], result.fitnessState?.exerciseStats?.[context.exerciseId]);
    // #6428: a coach's edit can change parts the summary does not hold (a scheme, a next weight).
    // Present only when the window showed the coach's parts; see design-docs/coach-exercise-editing.md
    const coachEdit = result.coachEdit?.subSlotId === context.subSlotId && context.coachOriginal ? result.coachEdit : undefined;
    const editedSubSlot = result.fitnessPlan?.subSlots?.[context.subSlotId];
    const setAsideId = context.coachOriginal?.setAsideExerciseId;
    const coachInput = coachEdit && editedSubSlot ? {
      original: { subSlot: context.coachOriginal.subSlot, stat: context.coachOriginal.stat, setAsideStat: context.coachOriginal.setAsideStat },
      edited: {
        subSlot: editedSubSlot,
        stat: result.fitnessState?.exerciseStats?.[context.exerciseId],
        setAsideStat: setAsideId ? result.fitnessState?.exerciseStats?.[setAsideId] : undefined,
      },
    } : undefined;
    // whether the coach changed anything at all; asked before any history is read
    const coachChanged = !!coachInput && collectCoachChanges(
      coachInput.original, coachInput.edited, { pathsRebuiltBySwitch: coachEdit.pathsRebuiltBySwitch, volume: coachEdit.volume },
    ).length > 0;
    if (isSameExerciseSettings(context.before, after) && !coachChanged) {
      return ConfigurationApplyOutcome.NothingToSave;
    }
    // if Edit Workouts removed the entry or gave it another exercise while the window was open
    const isEntryStillThere = (plan: FitnessPlan) => plan.subSlots?.[context.subSlotId]?.exercise_id === context.exerciseId;
    if (!isEntryStillThere((await this.loadPlanAndStateAsync()).plan)) {
      return ConfigurationApplyOutcome.ProgramChangedMeanwhile;
    }

    // #6428: the history the deload rule needs is read BEFORE anything is written, so a failed read
    // saves nothing. It travels up to handleConfigurationResultAsync, which reports it
    let history: RecentExerciseHistory | undefined;
    if (coachChanged) {
      history = await this.coachExerciseChange.readHistoryAsync(context.exerciseId, (await this.loadPlanAndStateAsync()).plan);
    }

    const transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.createCurrentPlanBackupLog(transaction);

    // the documents are loaded again AFTER the backup log's write, and both are changed and
    // committed with nothing awaited in between. a draft is a copy of its whole document, and
    // committing it writes the whole copy back, so a set the client concludes while a draft is
    // held would be undone
    const { plan, fitnessState } = await this.loadPlanAndStateAsync();
    if (!isEntryStillThere(plan)) {
      return ConfigurationApplyOutcome.ProgramChangedMeanwhile;
    }
    const planDraft = new CouchDocDraft(plan);
    const stateDraft = new CouchDocDraft(fitnessState);
    stateDraft.draft.exerciseStats ??= {};
    // #6428: the coach's parts go first, then today's merge. Nothing below awaits: the comment above is why
    const carried = applyExerciseSettingsSave(
      planDraft.draft.subSlots[context.subSlotId], stateDraft.draft.exerciseStats, context.exerciseId, context.before, after,
      result.fitnessPlan.subSlots[context.subSlotId],
      coachChanged ? {
        beforeSubSlot: coachInput.original.subSlot,
        editedSubSlot: coachInput.edited.subSlot,
        pathsRebuiltBySwitch: coachEdit.pathsRebuiltBySwitch,
        volumeChanged: !!coachEdit.volume,
        setAsideExerciseId: setAsideId,
        beforeSetAsideStat: coachInput.original.setAsideStat,
        editedSetAsideStat: coachInput.edited.setAsideStat,
        exerciseId: context.exerciseId,
        beforeStat: coachInput.original.stat,
        editedStat: coachInput.edited.stat,
      } : undefined);
    if (carried === CoachCarryOutcome.ChangedMeanwhile) {
      return ConfigurationApplyOutcome.ProgramChangedMeanwhile;
    }
    let prepared: PreparedChange | undefined;
    if (coachChanged) {
      prepared = prepareChange({
        subSlotId: context.subSlotId, ...coachInput, pathsRebuiltBySwitch: coachEdit.pathsRebuiltBySwitch, volume: coachEdit.volume,
        historyNewestFirst: history?.entriesNewestFirst ?? [],
        bodyWeight_kg: fitnessState.weight_kg, exerciseBw_pc: bodyWeightShareFromSessions(history?.sessionsNewestFirst),
        changed_utc: this.debug.getNowLocal().toUTC().toISO(), author: this.coachExerciseChange.author(),
      });
      applyPreparedChange(planDraft.draft.subSlots[context.subSlotId], prepared);
    }
    const committedPlan = planDraft.commitDraft();
    const committedState = stateDraft.commitDraft();

    await this.userDb.savePlanAsync(transaction, committedPlan);
    await this.userDb.saveFitnessStateAsync(transaction, committedState);
    if (prepared?.doc) {
      await this.coachExerciseChange.recordAsync([prepared.doc], transaction);
    }
    // the backlog cache is not cleared: no workout's structure changed
    return ConfigurationApplyOutcome.Saved;
  }

  /** Opens the settings window on a copy of the fresh plan and state, unless the row is stale. */
  private async openExerciseSettingsAsync(row: OverviewRow, returnCallback: string, onProgramChanged: () => void): Promise<void> {
    try {
      const { plan, fitnessState } = await this.loadPlanAndStateAsync();
      const subSlot = row.subSlotId ? plan.subSlots?.[row.subSlotId] : undefined;
      // if the program was edited since the grid was drawn, the row no longer names a real entry
      if (!row.subSlotId || !subSlot || subSlot.exercise_id !== row.exerciseId) {
        this.toastCtrl.showToast({ message: LL.trainingOverview.programChanged() });
        onProgramChanged();
        return;
      }
      const setAsideExerciseId = setAsideExerciseIdOf(subSlot);
      const context: ExerciseSettingsContext = {
        subSlotId: row.subSlotId,
        exerciseId: row.exerciseId,
        before: summarizeExerciseSettings(subSlot, fitnessState.exerciseStats?.[row.exerciseId]),
        // #6428: what the coach's parts compare against at save
        coachOriginal: {
          subSlot: deepClone(subSlot),
          stat: deepClone(fitnessState.exerciseStats?.[row.exerciseId]),
          setAsideExerciseId,
          setAsideStat: setAsideExerciseId ? deepClone(fitnessState.exerciseStats?.[setAsideExerciseId]) : undefined,
        },
      };
      await this.navCtrl.pushAsync(IExerciseConfigurationPage, {
        subSlotId: row.subSlotId,
        returnCallback,
        // the page still decides by manage mode whether to show the coach's parts
        coachEditable: true,
        local: { fitnessPlan: deepClone(plan), fitnessState: deepClone(fitnessState), returnContext: context },
      });
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.trainingOverview.loadFailed());
    }
  }

  /** the plan and fitness state as stored now; the service has no watcher of its own, so it borrows one for the read */
  private async loadPlanAndStateAsync() {
    return await this.userDb.database.withDocWatcherAsync(async watcher => ({
      plan: await this.userDb.loadPlanAsync(watcher),
      fitnessState: await this.userDb.loadFitnessStateAsync(watcher),
    }));
  }
}
